import React from "react";
import { Alert as JoyUiAlert, Box } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { CheckCircle, AlertCircle, AlertTriangle } from "lucide-react";
import Button from "../Button";
import { useApiResponseParser } from "../../hooks";

interface IAlertProps {
    successMessage?: string | React.ReactElement | null | false;
    errorMessage?: any; // Generic any to handle all error types
    warningMessage?: string | React.ReactElement | null | false;
    sx?: SxProps;
    [key: string]: any;
}

export default function Alert(props: IAlertProps) {
    const { successMessage, errorMessage, warningMessage, sx, ...otherProps } = props;
    const { parseErrorResponseData } = useApiResponseParser();

    // Reusable styles
    const defaultSx: SxProps = {
        border: "1px solid",
        borderColor: "divider",
    };

    const mergedSx: SxProps = sx ? [defaultSx, ...(Array.isArray(sx) ? sx : [sx])] : defaultSx;

    const errorDisplayMessage = parseErrorResponseData(errorMessage);

    const handleReport = () => {
        // Raw access for more debugging logs
        const raw = errorMessage?.response?.data || {};
        const reportData = {
            ErrorName: raw.Error?.Name || "BackendError",
            ErrorMessage: errorDisplayMessage || "Unknown Error",
            RequestId: raw.MetaData?.RequestId,
            InitTimestamp: raw.MetaData?.InitTimestamp,
            CompleteTimestamp: raw.MetaData?.CompleteTimestamp,
            StackTrace: raw.Error?.Stack,
        };

        console.log("---------- ERROR REPORT ----------");
        console.table(reportData);
        console.log("Full Raw Data:", raw);
        console.log("----------------------------------");
        alert("Detailed error report has been logged to the console for support processing.");
    };

    if (errorDisplayMessage) {
        return (
            <JoyUiAlert
                variant="soft"
                startDecorator={<AlertCircle size={20} />}
                color="danger"
                sx={{
                    ...mergedSx,
                    alignItems: "flex-start",
                    gap: 1.5,
                }}
                {...otherProps}
            >
                <Box sx={{ flex: 1 }}>
                    {errorDisplayMessage}
                    <Box mt={1}>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={handleReport}
                            sx={{
                                borderRadius: 8,
                                backgroundColor: "rgba(255, 255, 255, 0.5)",
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
                                alignSelf: { xs: "stretch", sm: "center" },
                            }}
                        >
                            Report
                        </Button>
                    </Box>
                </Box>
            </JoyUiAlert>
        );
    }

    if (warningMessage) {
        const warningContent = React.isValidElement(warningMessage) ? warningMessage : String(warningMessage);
        return (
            <JoyUiAlert
                variant="soft"
                startDecorator={<AlertTriangle size={24} />}
                color="warning"
                sx={mergedSx}
                {...otherProps}
            >
                {warningContent}
            </JoyUiAlert>
        );
    }

    if (successMessage) {
        const successContent = React.isValidElement(successMessage) ? successMessage : String(successMessage);
        return (
            <JoyUiAlert
                startDecorator={<CheckCircle size={20} />}
                variant="soft"
                color="success"
                sx={mergedSx}
                {...otherProps}
            >
                {successContent}
            </JoyUiAlert>
        );
    }

    return null;
}
